import { Control, FieldError, FieldPath, FieldValues, useController, UseControllerProps } from 'react-hook-form';
import {
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormGroup,
  FormHelperText,
  useForkRef,
} from '@mui/material';
import { useFormError } from '../../../contexts/FormErrorProvider';
import { forwardRef, ReactNode, Ref, RefAttributes } from 'react';

export type CheckboxElementProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<CheckboxProps, 'name'> & {
  validation?: UseControllerProps<TFieldValues, TName>['rules'];
  name: TName;
  parseError?: (error: FieldError) => ReactNode;
  label?: FormControlLabelProps['label'];
  helperText?: string;
  control: Control<TFieldValues>;
  labelProps?: Omit<FormControlLabelProps, 'label' | 'control'>;
};

type CheckboxElementComponent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: CheckboxElementProps<TFieldValues, TName> & RefAttributes<HTMLDivElement>,
) => JSX.Element;

const CheckboxElement = forwardRef(function CheckboxElement<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: CheckboxElementProps<TFieldValues, TName>, ref: Ref<HTMLDivElement>): JSX.Element {
  const {
    name,
    validation = {},
    required,
    label,
    control,
    helperText,
    labelProps,
    inputRef,
    indeterminate = false,
    ...rest
  } = props;


  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    disabled: rest.disabled,
    rules: required
      ? { ...validation, required: validation.required || 'This field is required' }
      : validation,
  });

  const handleInputRef = useForkRef(field.ref, inputRef);

  return (
    <FormControl required={required} error={!!error} ref={ref}>
      <FormGroup row>
        <FormControlLabel
          {...labelProps}
          label={label || ''}
          control={
            <Checkbox
              {...rest}
              {...field}
              indeterminate={indeterminate}
              color={rest.color || 'primary'}
              sx={[
                ...(Array.isArray(rest.sx) ? rest.sx : [rest.sx]),
                {
                  color: error ? 'error.main' : undefined,
                },
              ]}
              checked={!!field.value}
              inputRef={handleInputRef}
            />
          }
        />
      </FormGroup>
      {(error?.message || helperText) && (
        <FormHelperText error={!!error}>
          {error?.message || helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
});
CheckboxElement.displayName = 'CheckboxElement';

export default CheckboxElement as CheckboxElementComponent;
